import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import AddApplication from './AddApplication';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import { ApplicationObject } from '@/pages/apps/applications/ECommerceApi';
import useUser from '@auth/useUser';
import SelectAddressDialog from './SelectAddressDialog';

export const lineItemSchema = z
	.object({
		area: z.string().min(1, 'Area is required.'),
		name: z.string().optional(),
		images: z
			.array(
				z.union([
					z.instanceof(File),
					z.object({
						product_image: z.string().url()
					})
				])
			)
			.optional(),
		area_image: z
			.union([
				z.instanceof(File),
				z.string(),
				z.object({
					product_image: z.string()
				})
			])
			.optional(),
		quantity: z.preprocess(
			(val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
			z.number().min(1, 'Quantity must be at least 1').optional()
		),
		condition: z.string().optional(),
		purchased_date: z.string().optional(),
		purchased_cost: z.preprocess(
			(val) => (val === '' ? undefined : Number(val)),
			z.number().optional()
		),
		current_cost: z.preprocess(
			(val) => (val === '' ? undefined : Number(val)),
			z.number().optional()
		),
		model: z.string().optional(),
		other_area: z.string().optional(),
		other_line_item_name: z.string().optional(),
		description: z.string().max(500, 'Description must be at most 500 characters.').optional(),
		parentIndex: z.number().optional()
	})
	.superRefine((data, ctx) => {
		// If this is a child item (has parentIndex), validate all required fields
		if (typeof data.parentIndex === 'number') {
			// Child items need all fields
			if (!data.name || data.name.trim() === '') {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Item is required.',
					path: ['name']
				});
			}

			console.log("data.images___",data.images)
		
			if (data.images && data.images.length > 0) {
				// Validate image files
				data.images.forEach((file, index) => {
					if (file instanceof File) {
						if (file.size > 5 * 1024 * 1024) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ['images', index],
								message: 'File size should be less than 5MB'
							});
						}

						const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
						if (!validTypes.includes(file.type)) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ['images', index],
								message: 'Only .jpg, .png, and .webp formats are supported'
							});
						}
					}
				});
			}

			// Optional validation for condition - only validate if provided
			// No required validation for condition anymore

			// Optional validation for purchased_date - only validate if provided
			if (data.purchased_date && data.purchased_date.trim() !== '') {
				const today = new Date().toISOString().split('T')[0];
				const minDate = '1970-01-01';
				
				if (data.purchased_date > today) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Purchase Date cannot be in the future',
						path: ['purchased_date']
					});
				} else if (data.purchased_date < minDate) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Purchase Date cannot be before 1970',
						path: ['purchased_date']
					});
				}
			}

			// Optional validation for purchased_cost - only validate if provided
			if (data.purchased_cost !== null && data.purchased_cost !== undefined) {
				if (data.purchased_cost < 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Purchase Cost cannot be negative.',
						path: ['purchased_cost']
					});
				}
			}

			// Optional validation for current_cost - only validate if provided
			if (data.current_cost !== null && data.current_cost !== undefined) {
				if (data.current_cost < 0) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Current Cost cannot be negative.',
						path: ['current_cost']
					});
				}
			}
		}

		// Common validations for both parent and child items
		// Only validate other_area for parent items (items without parentIndex)
		if (typeof data.parentIndex === 'undefined' && data.area === 'Other' && !data.other_area?.trim()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Other Area is required.',
				path: ['other_area']
			});
		}

		// Only validate other_line_item_name for child items (items with parentIndex)
		if (typeof data.parentIndex === 'number' && data.name === 'Other' && !data.other_line_item_name?.trim()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Other Item is required.',
				path: ['other_line_item_name']
			});
		}
	});

export const schema = z.object({
	lineItems: z.array(lineItemSchema)
		.min(1, 'At least one line item is required.')
		.superRefine((lineItems, ctx) => {
			// Check if there's at least one child item (with parentIndex)
			const hasChildItems = lineItems.some(item => typeof item.parentIndex === 'number');
			if (!hasChildItems) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'At least one item with details is required.',
					path: []
				});
			}

			// Check that each area has at least one child item
			const parentItems = lineItems.filter(item => typeof item.parentIndex === 'undefined');
			const childItems = lineItems.filter(item => typeof item.parentIndex === 'number');
			
			// Group child items by their parent's area
			const childItemsByArea = new Map<string, number>();
			childItems.forEach(child => {
				const parentIndex = child.parentIndex!;
				const parent = lineItems[parentIndex];
				if (parent && parent.area) {
					const areaName = parent.area;
					childItemsByArea.set(areaName, (childItemsByArea.get(areaName) || 0) + 1);
				}
			});

			// Check each parent area has at least one child
			parentItems.forEach((parent, parentIndex) => {
				if (parent.area && parent.area !== 'Other') {
					const childCount = childItemsByArea.get(parent.area) || 0;
					if (childCount === 0) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: `Area "${parent.area}" must have at least one item.`,
							path: ['lineItems', parentIndex, 'area']
						});
					}
				}
			});
		})
});

/**
 * Type for a single line item
 */
type LineItemFormData = z.infer<typeof lineItemSchema>;
// type LineItem = z.infer<typeof lineItemSchema>;

/**
 * The application.
 */
function Application() {
	const [lineItems, setLineItems] = useState<Record<number, LineItemFormData>>({});
	const { data: user } = useUser();
	const [searchParams] = useSearchParams();
	const view = searchParams.get('view') === 'true';

	type RouteParams = {
		applicationId: 'new' | string;
	};
	const routeParams = useParams<RouteParams>();
	const { applicationId } = routeParams;
	const navigate = useNavigate();
	const [deletedId, setDeletedId] = useState([]);
	const [selectedAddress, setSelectedAddress] = useState<{
		id: number;
		name: string | null;
		location_name: string | null;
		type: string | null;
		documents?: Array<{
			id: number;
			address_id: number;
			file_name: string;
			file_type: string;
			document_file_url: string;
		}>;
	} | null>(null);
	const [showAddressDialog, setShowAddressDialog] = useState(false);

	const methods = useForm({
		mode: 'onChange',
		resolver: zodResolver(schema),
		defaultValues: {
			lineItems: []
		}
	});

	// Show address dialog when creating new application
	useEffect(() => {
		if (applicationId === 'new' && !selectedAddress && !showAddressDialog) {
			setShowAddressDialog(true);
		}
	}, [applicationId, selectedAddress, showAddressDialog]);

	const handleAddressSelect = (address: {
		id: number;
		name: string | null;
		location_name: string | null;
		type: string | null;
		documents?: Array<{
			id: number;
			address_id: number;
			file_name: string;
			file_type: string;
			document_file_url: string;
		}>;
	}) => {
		setSelectedAddress(address);
		setShowAddressDialog(false);
	};

	const handleCloseAddressDialog = () => {
		setShowAddressDialog(false);
		// Redirect back to applications list if user cancels
		if (applicationId === 'new') {
			navigate('/apps/applications');
		}
	};

	// For existing applications, show form directly (no address selection needed)
	if (applicationId !== 'new') {
		return (
			<FormProvider {...methods}>
				<AddApplication
					setLineItems={setLineItems as React.Dispatch<React.SetStateAction<Record<number, ApplicationObject>>>}
					lineItems={lineItems}
					isView={(applicationId !== 'new' && user?.role === 'Admin') || view}
					setDeletedId={setDeletedId}
					deletedId={deletedId}
				/>
			</FormProvider>
		);
	}

	// For new applications, show address dialog first
	return (
		<>
			<SelectAddressDialog
				open={showAddressDialog}
				onClose={handleCloseAddressDialog}
				onSelect={handleAddressSelect}
			/>
			{selectedAddress && (
				<FormProvider {...methods}>
					<AddApplication
						setLineItems={setLineItems as React.Dispatch<React.SetStateAction<Record<number, ApplicationObject>>>}
						lineItems={lineItems}
						isView={(applicationId !== 'new' && user?.role === 'Admin') || view}
						setDeletedId={setDeletedId}
						deletedId={deletedId}
						addressId={selectedAddress.id}
						selectedAddress={selectedAddress}
						onRequestChangeAddress={() => setShowAddressDialog(true)}
					/>
				</FormProvider>
			)}
		</>
	);
}

export default Application;
